import {HttpClient, HttpHeaders, HttpResponse} from "@angular/common/http";
import {Observable, of, throwError} from "rxjs";
import {environment} from "../../../environments/environment";
import {CaseNotes, DeclineTransfer, Order, StandardName} from "../../store/state/order.state";
import {Translations} from "../../order/translations/translations.model";
import {OrderReviewChecklist, PreviousCbConfirmation, TransferOrderReviewChecklist} from "../../store/state/order-review-checklist.state";
import {User} from "../../authentication/authentication.service";
import {AccountInfo} from "../../store/state/account.state";
import {catchError} from "rxjs/operators";
import {IntermediatePriceAdjustment, PriceAdjustmentsConfig} from "../../order/components/order-pricing/pricing-adjustments/pricing-adjustments.component";

export class ReviewEndpoints {

	constructor(private http: HttpClient) {
	}

	getOrdersForApplicationReview(): Observable<[]> {
		const url: string = `${environment.apiUrl}/application-review/orders`;
		return this.http.get<[]>(url, {responseType: "json"});
	}

	getClientDataTranslations(correlationId: string): Observable<Translations[]> {
		return this.http.get<Translations[]>(`${environment.apiUrl}/translations/${correlationId}`);
	}

	saveTranslationsDraft(correlationId: string, translations: Translations) {
		return this.http.put(`${environment.apiUrl}/translations/edit/${correlationId}`, translations, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	saveTranslations(correlationId: string, translations: Translations, allowPartialStore: boolean) {
		return this.http.put(`${environment.apiUrl}/translations/${correlationId}?allowPartialStore=${allowPartialStore}`, translations,
			{headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	getEmailToCbPreview(correlationId: string, standardName: StandardName, certNumber: string): Observable<string> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/standard/${standardName}/email-to-cb-preview?certificateNumber=${certNumber}`;
		return this.http.get(url, {responseType: "text", observe: "body"});
	}

	sendEmailToPreviousCertBody(correlationId: string, email: string, certificateNumber: string, standardName: string): Observable<TransferOrderReviewChecklist> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/send-email-to-cb`;
		return this.http.post<TransferOrderReviewChecklist>(url, {
			orderCorrelationId: correlationId,
			email,
			certificateNumber,
			standardName
		}, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	uploadCbAnswerFile(correlationId: string, standardName: string, file: File): Observable<OrderReviewChecklist> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/standard/${standardName}/upload-answer-file-from-cb`;
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<OrderReviewChecklist>(url, formData);
	}

	deleteCbAnswerFile(correlationId: string, standardName: string): Observable<OrderReviewChecklist> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/standard/${standardName}/remove-uploaded-answer-file-from-cb`;
		return this.http.post<OrderReviewChecklist>(url, null);
	}

	setNoAnswerFromCbFlag(correlationId: string, standardName: string, noAnswerFromCB: boolean): Observable<OrderReviewChecklist> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/standard/${standardName}/no-answer-from-cb/${noAnswerFromCB}`;
		return this.http.post<OrderReviewChecklist>(url, null);
	}

	setDataComparedFlag(correlationId: string, dataCompared: boolean): Observable<any> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/data-compared/${dataCompared}`;
		return this.http.post<any>(url, null);
	}

	setCbAnswerText(correlationId: string, standardName: string, text: string): Observable<OrderReviewChecklist> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/standard/${standardName}/set-answer-text-from-cb`;
		return this.http.post<OrderReviewChecklist>(url, text, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	startApplicationReview(correlationId: string): Observable<any> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/start-review`;
		return this.http.put(url, null, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	saveChecklist(checklist: OrderReviewChecklist, isTransfer: boolean): Observable<any> {
		const url: string = `${environment.apiUrl}/application-review/${checklist.orderCorrelationId}/checklist/${isTransfer ? 'transfer' : 'initial-certification'}`;
		return this.http.put(url, checklist, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	acceptApplicationReview(correlationId: string, checklist: OrderReviewChecklist, isTransfer: boolean, opportunityId: string): Observable<any> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/accept-review/${isTransfer ? 'transfer' : 'initial-certification'}?opportunityId=${opportunityId}`;
		return this.http.post(url, checklist, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	declineApplicationReview(correlationId: string, reason: string, checklist: OrderReviewChecklist, opportunityId: string | null): Observable<any> {
		const urlParam = !!opportunityId ? `?opportunityId=${opportunityId}` : '';
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/decline-review${urlParam}`;
		return this.http.post(url, {
			reason,
			checklist
		}, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	startChangeProposal(correlationId: string): Observable<Order> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/start-change-proposal`;
		return this.http.post<Order>(url, null);
	}

	saveChangeProposal(order: Order): Observable<Order> {
		const url: string = `${environment.apiUrl}/application-review/${order.orderCorrelationId}/save-change-proposal`;
		return this.http.post<Order>(url, order, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	restoreTransferDocuments(correlationId: string): Observable<Order> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/restore-transfer-documents`;
		return this.http.post<Order>(url, correlationId, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	cancelChangeProposal(correlationId: string): Observable<Order> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/cancel-change-proposal`;
		return this.http.post<Order>(url, null);
	}

	sendChangeProposalToClient(correlationId: string, checklist: OrderReviewChecklist): Observable<Order> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/send-change-proposal`;
		return this.http.post<Order>(url, checklist, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	uploadCertificateFiles(correlationId: string, files: File[]): Observable<Order> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/add-certificate-documents`;
		const formData = new FormData();
		files.forEach(file => formData.append("files", file));
		return this.http.post<Order>(url, formData);
	}

	uploadReportFiles(correlationId: string, files: File[]): Observable<Order> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/add-report-documents`;
		const formData = new FormData();
		files.forEach(file => formData.append("files", file));
		return this.http.post<Order>(url, formData);
	}

	removeTransferFile(correlationId: string, fileUuid: string): Observable<Order> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/delete-transfer-document?fileUuid=${fileUuid}`;
		return this.http.post<Order>(url, null);
	}

	sendSalesForceQuoteData(correlationId: string, opportunityId: string): Observable<any> {
		const url: string = `${environment.apiUrl}/sales-force/${correlationId}/send?opportunityId=${opportunityId}`;
		return this.http.post<any>(url, null, {observe: "body", responseType: "json"});
	}

	getClientsAccountInfo(correlationId: string): Observable<AccountInfo> {
		const url: string = `${environment.apiUrl}/application-review/account-info/order/${correlationId}`;
		return this.http.get<AccountInfo>(url).pipe(catchError(error => error.status == 403 ? of({} as AccountInfo) : throwError(error)));
	}

	getChecklist(correlationId: string): Observable<OrderReviewChecklist> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/checklist`;
		return this.http.get<OrderReviewChecklist>(url, {responseType: "json"})
	}

	getOrderForApplicationReview(correlationId: string): Observable<Order> {
		const url: string = `${environment.apiUrl}/application-review/order/${correlationId}`;
		return this.http.get<Order>(url, {responseType: "json"});
	}

	reassignApplicationReviewer(correlationId: string, newAssignee: string): Observable<User> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/reassign`;
		return this.http.post<User>(url, newAssignee, {responseType: "json"});
	}

	getIntermediatePriceAdjustments(accBody: string): Observable<IntermediatePriceAdjustment[]> {
		const url: string = `${environment.apiUrl}/application-review/intermediate-price-adjustments?accBody=${accBody}`;
		return this.http.get<IntermediatePriceAdjustment[]>(url, {
			responseType: "json",
			headers: {'Content-Type': 'application/json;charset=UTF-8'}
		});
	}

	getPriceAdjustmentsConfig(country: string, accBody: string): Observable<PriceAdjustmentsConfig> {
		const url: string = `${environment.apiUrl}/application-review/price-adjustments-config?country=${country}&accBody=${accBody}`;
		return this.http.get<PriceAdjustmentsConfig>(url, {
			responseType: "json",
			headers: {'Content-Type': 'application/json'}
		});
	}

	getApplicationReviewersForReassigning(correlationId: string): Observable<User[]> {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/reassign/available-reviewers`;
		return this.http.get<User[]>(url, {responseType: "json"});
	}

	getTechnicalReviewersForReassigning(correlationId: string): Observable<User[]> {
		const url: string = `${environment.apiUrl}/technical-review/${correlationId}/reassign/available-reviewers`;
		return this.http.get<User[]>(url, {responseType: "json"});
	}

	downloadCbAnswerFile(orderCorrelationId: string, standardName: string): Observable<HttpResponse<Blob>> {
		const url: string = `${environment.apiUrl}/application-review/${orderCorrelationId}/standard/${standardName}/download-answer-file-from-cb`;
		return this.http.get(url, {responseType: 'blob' as 'blob', observe: "response"});
	}

	getActiveOrderDate(correlationId: string) {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/active-order-date`;
		return this.http.get<string>(url, {responseType: "json", headers: {'Content-Type': 'application/json;charset=UTF-8'}});
	}

	savePreArMessage(correlationId: string, message: string, createdAt: Date, createdBy: string) {
		const url: string = `${environment.apiUrl}/application-review/${correlationId}/save-pre-ar-message`;
		const payload = {
			message: message,
			createdAt: createdAt.toISOString(),
			createdBy: createdBy
		}
		return this.http.post<User>(url, payload, {responseType: "json"});
	}
}
